import { CheckCircle2, Heart, Info, Layers3, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";

import { CelebrationIcon } from "@/components/icons/CelebrationIcon";
import type { BundleWithCourses } from "@/lib/schema";
import { cn } from "@/lib/utils";

const signatureRoutineLabel = "Signature Routine";

const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

interface LibraryBundleCardProps {
  bundle: BundleWithCourses;
  instructorImage: string;
  isLoggedIn: boolean;
  isOwned: boolean;
  isSaved: boolean;
  inCart: boolean;
  hasCartConflict: boolean;
  ownedCourseCount: number;
  loginHref: string;
  onAddToCart: (bundle: BundleWithCourses) => void;
  onOpenPreview: () => void;
  onToggleSaved: () => void;
}

export function LibraryBundleCard({
  bundle,
  instructorImage,
  isLoggedIn,
  isOwned,
  isSaved,
  inCart,
  hasCartConflict,
  ownedCourseCount,
  loginHref,
  onAddToCart,
  onOpenPreview,
  onToggleSaved,
}: LibraryBundleCardProps) {
  const primaryLabel = getPrimaryLabel({ isLoggedIn, isOwned, inCart, hasCartConflict });
  const previewNames = bundle.courses.slice(0, 3).map((course) => course.name);
  const instructorMeta = getBundleInstructorMeta(bundle);
  const hasVisibleDiscount = hasVisibleDiscountPercentage(bundle.discount_percentage);

  return (
    <article className="relative flex h-full flex-col rounded-[1.5rem] bg-white p-[2rem_1.5rem_1.5rem] shadow-[0_24px_55px_rgba(74,25,60,0.07)] max-[479px]:rounded-[1.25rem] max-[479px]:p-[2rem_1rem_1rem]">
      <div className="absolute -left-4 top-0 z-[1] -translate-y-1/2 flex items-center gap-1 rounded-full bg-white px-[0.88rem] py-2 text-[0.88rem] font-semibold text-[#762a5c] whitespace-nowrap shadow-[0_0_0_6px_var(--background-color--background-primary,#fafafa),inset_0_0_0_1px_rgba(255,167,36,0.1)]">
        <CelebrationIcon className="h-4 w-4 shrink-0" />
        <span className="bundle-badge-text">{signatureRoutineLabel}</span>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-[0.8rem]">
          <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full">
            <img
              src={instructorImage}
              alt={instructorMeta.name}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="min-w-0">
            <div className="text-[0.65rem] font-bold uppercase tracking-[0.06em] text-[#b0a4aa]">
              {instructorMeta.label}
            </div>
            <div className="text-[1rem] font-medium leading-[1.5] text-[#2f2830]">
              {instructorMeta.name}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center border-none bg-transparent text-[#3d3439] transition-colors duration-[180ms] ease-in-out hover:text-[#762a5c]"
            aria-label={`More info about ${bundle.name}`}
            onClick={onOpenPreview}
          >
            <Info className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={cn(
              "inline-flex h-6 w-6 items-center justify-center border-none bg-transparent text-[#3d3439] transition-colors duration-[180ms] ease-in-out hover:text-[#762a5c]",
              isSaved && "text-[#762a5c]",
            )}
            aria-label={isSaved ? "Remove signature routine from wishlist" : "Add signature routine to wishlist"}
            onClick={onToggleSaved}
          >
            <Heart className={cn("h-4 w-4", isSaved && "fill-current")} />
          </button>
        </div>
      </div>

      <button
        type="button"
        className="relative mt-4 w-full overflow-hidden rounded-[1rem] bg-[#ece7ea] [aspect-ratio:16/9]"
        onClick={onOpenPreview}
      >
        {bundle.thumbnail_url ? (
          <>
            <div
              className="absolute inset-0 scale-125 bg-cover bg-center opacity-75 blur-[18px]"
              style={{ backgroundImage: `url(${JSON.stringify(bundle.thumbnail_url)})` }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-[#2d1024]/15" aria-hidden="true" />
            <img
              src={bundle.thumbnail_url}
              alt={bundle.name}
              className="relative z-[1] h-full w-full object-contain object-center"
              loading="lazy"
            />
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,#dca4be_0%,#91557a_45%,#4f2343_100%)] text-white">
            <Layers3 className="h-10 w-10" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#2d1024]/55 via-transparent to-transparent" />
      </button>

      <div className="mt-4">
        <div>
          <div className="text-[0.78rem] font-bold uppercase tracking-[0.08em] text-[#a68f9c]">
            {bundle.courses.length} tutorial{bundle.courses.length === 1 ? "" : "s"} + MP3 Music Mix
          </div>
          <h3 className="mt-2 font-display text-[2rem] leading-none text-[#341928] max-[767px]:text-[1.7rem]">
            {bundle.name}
          </h3>
        </div>
      </div>

      <div className="mt-4 flex items-end justify-between gap-4 rounded-[1.1rem] bg-[#faf6f8] px-4 py-3">
        <div>
          <div className="text-[0.74rem] font-bold uppercase tracking-[0.08em] text-[#af97a4]">
            {getBundlePriceEyebrow(bundle)}
          </div>
          <div className={cn(
            "font-semibold leading-none text-[#341928]",
            hasVisibleDiscount ? "mt-2 text-[1.65rem]" : "mt-1 text-[1.65rem]",
          )}>
            {priceFormatter.format(bundle.price)}
          </div>
        </div>
        {ownedCourseCount > 0 ? (
          <div className="rounded-full bg-[#eef8f0] px-3 py-1 text-[0.78rem] font-semibold text-[#2f7d46]">
            {ownedCourseCount} already owned
          </div>
        ) : null}
      </div>

      <p className="mt-4 text-[1rem] leading-[1.65] text-[#5a5658] sm:text-[1.08rem]">
        {bundle.short_description ?? bundle.long_description ?? "Curated tutorials packaged together at one set price."}
      </p>

      <div className="mt-4 flex flex-wrap gap-[0.45rem]">
        {previewNames.map((name) => (
          <span
            key={name}
            className="inline-flex items-center rounded-full border border-[#d9c9d3] bg-white px-[0.72rem] py-[0.38rem] text-[0.82rem] font-semibold text-[#6a5862]"
          >
            {name}
          </span>
        ))}
        {bundle.courses.length > previewNames.length ? (
          <span className="inline-flex items-center rounded-full border border-dashed border-[#d9c9d3] px-[0.72rem] py-[0.38rem] text-[0.82rem] font-semibold text-[#8a7580]">
            +{bundle.courses.length - previewNames.length} more
          </span>
        ) : null}
      </div>

      {ownedCourseCount > 0 && !isOwned ? (
        <div className="mt-4 rounded-[1rem] border border-[#d5eadb] bg-[#f4fbf6] px-4 py-3 text-[0.88rem] font-medium text-[#2c6d41]">
          You already own {ownedCourseCount} tutorial{ownedCourseCount === 1 ? "" : "s"} included in this signature routine. The signature routine still keeps its full price.
        </div>
      ) : null}

      <div className="mt-auto pt-5">
        {!isLoggedIn && !isOwned ? (
          <Link
            to={loginHref}
            className="inline-flex min-h-[51px] w-full items-center justify-center gap-2 rounded-full border border-[#762a5c] bg-[#762a5c] px-8 py-4 text-[14px] font-medium leading-none text-white no-underline transition-colors hover:bg-[#5f2349]"
          >
            {primaryLabel}
          </Link>
        ) : isOwned ? (
          <button
            type="button"
            className="inline-flex min-h-[51px] w-full cursor-default items-center justify-center gap-2 rounded-full border border-[#cef5ca] bg-[#cef5ca] px-8 py-4 text-[14px] font-medium leading-none text-[#114e0b] opacity-75"
            disabled
          >
            <CheckCircle2 className="h-4 w-4" />
            {primaryLabel}
          </button>
        ) : (
          <button
            type="button"
            className={cn(
              "inline-flex min-h-[51px] w-full items-center justify-center gap-2 rounded-full border px-8 py-4 text-[14px] font-medium leading-none transition-colors",
              inCart
                ? "border-[#762a5c] bg-white text-[#762a5c] hover:bg-[#f3edf1]"
                : hasCartConflict
                  ? "cursor-not-allowed border-[#d7c3d0] bg-[#f7f2f5] text-[#9a8b93]"
                  : "border-[#762a5c] bg-[#762a5c] text-white hover:bg-[#5f2349]",
            )}
            disabled={hasCartConflict}
            onClick={() => onAddToCart(bundle)}
          >
            <ShoppingCart className="h-4 w-4" />
            {primaryLabel}
          </button>
        )}
      </div>
    </article>
  );
}

function getPrimaryLabel({
  isLoggedIn,
  isOwned,
  inCart,
  hasCartConflict,
}: {
  isLoggedIn: boolean;
  isOwned: boolean;
  inCart: boolean;
  hasCartConflict: boolean;
}) {
  if (isOwned) return "Purchased";
  if (!isLoggedIn) return "Login to Purchase";
  if (inCart) return "Added to Cart";
  if (hasCartConflict) return "Resolve Cart to Add";
  return "Add to Cart";
}

function getBundleInstructorMeta(bundle: BundleWithCourses) {
  const instructorNames = Array.from(
    new Set(
      bundle.courses
        .map((course) => course.Instructor?.name?.trim())
        .filter((name): name is string => Boolean(name)),
    ),
  );

  if (instructorNames.length === 0) {
    return { label: "Instructor", name: "Khusbu Gemmati" };
  }

  if (instructorNames.length === 1) {
    return { label: "Instructor", name: instructorNames[0] };
  }

  return {
    label: "Instructors",
    name: `${instructorNames[0]} + ${instructorNames.length - 1} more`,
  };
}

function getBundlePriceEyebrow(bundle: BundleWithCourses) {
  if (!hasVisibleDiscountPercentage(bundle.discount_percentage)) return "Signature Routine price";

  return `Signature Routine at ${formatDiscountPercentage(bundle.discount_percentage!)} discount`;
}

function hasVisibleDiscountPercentage(value: number | null) {
  return value != null && value > 0;
}

function formatDiscountPercentage(value: number) {
  return Number.isInteger(value) ? `${value}%` : `${value.toFixed(2).replace(/\.?0+$/, "")}%`;
}
